import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createEmployee, getEmployeeTypeList } from "@/api/employee";
import { getSettingExcel } from "@/api/setting";
import { getShopCustomerGroup } from "@/api/shop";
import { useEmployeeStore } from "@/store/employee";
import { useShopStore } from "@/store/shop";
import { useAuthStore } from "@/store/auth";
import { toast } from 'sonner';
import type { EmployeeRegister, EmployeeType } from "@/types/employee";
import type { CustomerGroup } from "@/types/shop";

interface CreateEmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
  
export function CreateEmployeeDialog({ open, onOpenChange }: CreateEmployeeDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isOptionLoading, setIsOptionLoading] = useState(false);
  const [baseField, setBaseField] = useState<"email" | "phoneNumber">("email");
  const [formData, setFormData] = useState<EmployeeRegister>({
    shopNo: 1,
    employeeId: "",
    email: "",
    phoneNumber: "",
    name: "",
    employeeTypeId: 0,
    employeeType: undefined,
    groupNo: 0,
    isFixedGroup: "F",
    isResigned: false,
  });
  const [employeeTypes, setEmployeeTypes] = useState<EmployeeType[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const isSubmitDisabled = useMemo(() => isLoading || isOptionLoading, [isLoading, isOptionLoading]);

  const { selectedShop } = useShopStore();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.isSuperAdmin === true;
  const allowedEmployeeTypeId = user?.allowedEmployeeTypeId ?? null;
  const allowedEmployeeGroupNo = user?.allowedEmployeeGroupNo ?? null;
  const allowedEmployeeIsFixedGroup = user?.allowedEmployeeIsFixedGroup ?? null;
  const { fetchEmployees, searchParams } = useEmployeeStore();
  
  const isCafe24User = user?.platform === 'cafe24';

  const initializeDialogData = useCallback(async (shopNo: number) => {
    setIsOptionLoading(true);
    try {
      const [typeResponse, groupResponse, settingResponse] = await Promise.all([
        getEmployeeTypeList({ shopNo }),
        getShopCustomerGroup({ shopNo }),
        getSettingExcel({ shopNo, type: "upsert" })
      ]);

      let resolvedTypes: EmployeeType[] = [];
      if (typeResponse.code === 200 && typeResponse.data) {
        resolvedTypes = typeResponse.data.types ?? [];
        setEmployeeTypes(resolvedTypes);
      } else {
        setEmployeeTypes([]);
        if (typeResponse.message) {
          toast.error(typeResponse.message);
        } else {
          toast.error("임직원 구분 목록을 불러오는데 실패했습니다.");
        }
      }

      let resolvedGroups: CustomerGroup[] = [];
      if (groupResponse.code === 200 && groupResponse.data) {
        resolvedGroups = groupResponse.data;
        setCustomerGroups(resolvedGroups);
      } else {
        setCustomerGroups([]);
        toast.error("회원 등급 정보를 불러오는데 실패했습니다.");
      }

      let nextBaseField: "email" | "phoneNumber" = "email";
      let nextGroupNo = 0;
      let nextIsFixedGroup: "T" | "F" = "F";

      if (settingResponse.code === 200 && settingResponse.data) {
        nextBaseField = settingResponse.data.baseField;
        nextGroupNo = settingResponse.data.groupNo ?? 0;
        nextIsFixedGroup = settingResponse.data.isFixedGroup ? "T" : "F";
      } else {
        if (settingResponse.message) {
          toast.error(settingResponse.message);
        }
      }

      let nextEmployeeTypeId = resolvedTypes.length > 0 ? resolvedTypes[0].typeId : 0;

      const resolvedAllowedTypeId =
        !isSuperAdmin && allowedEmployeeTypeId !== null
          ? allowedEmployeeTypeId
          : undefined;

      const resolvedAllowedGroupNo =
        !isSuperAdmin && allowedEmployeeGroupNo !== null
          ? allowedEmployeeGroupNo
          : undefined;

      const resolvedAllowedIsFixedGroup =
        !isSuperAdmin && typeof allowedEmployeeIsFixedGroup === "boolean"
          ? allowedEmployeeIsFixedGroup
            ? "T"
            : "F"
          : undefined;

      if (resolvedAllowedTypeId !== undefined) {
        const matchedType = resolvedTypes.find((type) => type.typeId === resolvedAllowedTypeId);
        nextEmployeeTypeId = matchedType ? matchedType.typeId : resolvedAllowedTypeId;
      }

      if (resolvedAllowedGroupNo !== undefined) {
        nextGroupNo = resolvedAllowedGroupNo;
      } else if (resolvedGroups.length > 0) {
        const matchedGroup = resolvedGroups.find((group) => group.groupNo === nextGroupNo);
        if (!matchedGroup) {
          nextGroupNo = resolvedGroups[0].groupNo;
        }
      }

      if (resolvedAllowedIsFixedGroup !== undefined) {
        nextIsFixedGroup = resolvedAllowedIsFixedGroup;
      }

      setBaseField(nextBaseField);
      setFormData({
        shopNo,
        employeeId: "",
        email: "",
        phoneNumber: "",
        name: "",
        employeeTypeId: nextEmployeeTypeId,
        employeeType: undefined,
        groupNo: nextGroupNo,
        isFixedGroup: nextIsFixedGroup,
        isResigned: false,
      });
    } catch (error) {
      console.error("임직원 등록 데이터 초기화 오류:", error);
      toast.error("임직원 등록 정보를 가져오는 중 오류가 발생했습니다.");
      setEmployeeTypes([]);
      setCustomerGroups([]);
      setBaseField("email");
      setFormData({
        shopNo,
        employeeId: "",
        email: "",
        phoneNumber: "",
        name: "",
        employeeTypeId: 0,
        employeeType: undefined,
        groupNo: 0,
        isFixedGroup: "F",
        isResigned: false,
      });
    } finally {
      setIsOptionLoading(false);
    }
  }, [allowedEmployeeGroupNo, allowedEmployeeIsFixedGroup, allowedEmployeeTypeId, isSuperAdmin]);

  // 다이얼로그가 열릴 때마다 폼 초기화 및 설정 정보 가져오기
  useEffect(() => {
    if (!open) return;

    const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1;

    setEmployeeTypes([]);
    setCustomerGroups([]);
    setBaseField("email");
    const initialEmployeeTypeId =
      !isSuperAdmin && allowedEmployeeTypeId !== null ? allowedEmployeeTypeId : 0;
    const initialGroupNo =
      !isSuperAdmin && allowedEmployeeGroupNo !== null ? allowedEmployeeGroupNo : 0;
    const initialIsFixedGroup: "T" | "F" =
      !isSuperAdmin && typeof allowedEmployeeIsFixedGroup === "boolean"
        ? allowedEmployeeIsFixedGroup
          ? "T"
          : "F"
        : "F";

    setFormData({
      shopNo,
      employeeId: "",
      email: "",
      phoneNumber: "",
      name: "",
      employeeTypeId: initialEmployeeTypeId,
      employeeType: undefined,
      groupNo: initialGroupNo,
      isFixedGroup: initialIsFixedGroup,
      isResigned: false,
    });

    initializeDialogData(shopNo);
  }, [
    open,
    isCafe24User,
    selectedShop,
    initializeDialogData,
    isSuperAdmin,
    allowedEmployeeTypeId,
    allowedEmployeeGroupNo,
    allowedEmployeeIsFixedGroup,
  ]);

  const handleInputChange = (field: keyof EmployeeRegister, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 각 항목별 상세 검증
    if (!formData.employeeId.trim()) {
      toast.error("사원번호를 입력해주세요.");
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error("이름을 입력해주세요.");
      return;
    }

    if (!formData.employeeTypeId) {
      toast.error("임직원 구분을 선택해주세요.");
      return;
    }

    if (!formData.groupNo) {
      toast.error("회원 등급을 선택해주세요.");
      return;
    }
    
    // baseField에 따른 필수 입력 검증
    if (baseField === "phoneNumber") {
      // 휴대폰번호가 필수인 경우
      if (!formData.phoneNumber.trim()) {
        toast.error("휴대폰번호를 입력해주세요.");
        return;
      }
      
      // 휴대폰번호 형식 검증 (한국 휴대폰번호, 숫자만)
      const phoneNumber = formData.phoneNumber.trim();
      if (phoneNumber.length < 10 || phoneNumber.length > 11) {
        toast.error("휴대폰번호는 10-11자리 숫자를 입력해주세요.");
        return;
      }
      
      // 한국 휴대폰번호 패턴 검증 (010, 011, 016, 017, 018, 019로 시작)
      if (!phoneNumber.startsWith('01') || !/^01[016789]/.test(phoneNumber)) {
        toast.error("올바른 휴대폰번호를 입력해주세요. (010, 011, 016, 017, 018, 019로 시작)");
        return;
      }
    } else {
      // 이메일이 필수인 경우 (기본값)
      if (!formData.email.trim()) {
        toast.error("이메일을 입력해주세요.");
        return;
      }
      
      // 이메일 형식 검증
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("올바른 이메일 형식을 입력해주세요.");
        return;
      }
    }
    
    // 선택적 필드 검증 (필수가 아닌 경우에도 입력했다면 형식 검증)
    if (baseField === "email" && formData.phoneNumber.trim()) {
      // 이메일이 필수인데 휴대폰번호도 입력했다면 형식 검증
      const phoneNumber = formData.phoneNumber.trim();
      if (phoneNumber.length < 10 || phoneNumber.length > 11) {
        toast.error("휴대폰번호는 10-11자리 숫자를 입력해주세요.");
        return;
      }
      
      if (!phoneNumber.startsWith('01') || !/^01[016789]/.test(phoneNumber)) {
        toast.error("올바른 휴대폰번호를 입력해주세요. (010, 011, 016, 017, 018, 019로 시작)");
        return;
      }
    } else if (baseField === "phoneNumber" && formData.email.trim()) {
      // 휴대폰번호가 필수인데 이메일도 입력했다면 형식 검증
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("올바른 이메일 형식을 입력해주세요.");
        return;
      }
    }

    setIsLoading(true);
    try {
      const requestData: any = {
        shopNo: formData.shopNo,
        employeeId: formData.employeeId.trim(),
        name: formData.name.trim(),
        employeeTypeId: formData.employeeTypeId,
        groupNo: formData.groupNo,
        isFixedGroup: formData.isFixedGroup,
        isResigned: formData.isResigned
      };

      // 빈 값이 아닌 경우에만 API에 포함
      if (formData.phoneNumber && formData.phoneNumber.trim()) {
        requestData.phoneNumber = formData.phoneNumber.trim();
      }
      
      if (formData.email && formData.email.trim()) {
        requestData.email = formData.email.trim().toLowerCase();
      }

      const response = await createEmployee(requestData);
      
      if (response.code === 200 || response.code === 201) {
        // 성공 시 목록 새로고침
        await fetchEmployees({ ...searchParams });
        onOpenChange(false);
        toast.success("임직원이 성공적으로 등록되었습니다.");
      } else {
        toast.error(response.message || "임직원 등록에 실패했습니다.");
      }
    } catch (error) {
      console.error("임직원 등록 오류:", error);
      toast.error("임직원 등록 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>임직원 등록</DialogTitle>
            <DialogDescription>
              신규 임직원 정보를 등록할 수 있어요.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="employeeTypeId">구분</Label>
              <Select
                key={`employee-type-${employeeTypes.map(type => type.typeId).join("-")}`}
                value={formData.employeeTypeId ? String(formData.employeeTypeId) : undefined}
                onValueChange={(value) => {
                  const parsed = Number(value);
                  if (Number.isNaN(parsed)) return;
                  handleInputChange("employeeTypeId", parsed);
                }}
                disabled={isOptionLoading || employeeTypes.length === 0 || !isSuperAdmin}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="구분을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {employeeTypes.length === 0 ? (
                    <SelectItem value="no-type" disabled>
                      등록된 구분이 없습니다.
                    </SelectItem>
                  ) : (
                    employeeTypes.map((type) => (
                      <SelectItem key={type.typeId} value={type.typeId.toString()}>
                        {type.typeName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="employeeId">사원번호</Label>
              <Input
                id="employeeId"
                value={formData.employeeId}
                onChange={(e) => handleInputChange("employeeId", e.target.value)}
                placeholder="사원번호를 입력하세요"
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">이름</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="이름을 입력하세요"
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phoneNumber">
                휴대폰번호
              </Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => {
                  // 숫자만 입력 허용
                  const numericValue = e.target.value.replace(/\D/g, '');
                  handleInputChange("phoneNumber", numericValue);
                }}
                placeholder={
                  baseField === "phoneNumber" 
                    ? "휴대폰번호를 입력하세요 (필수) (예: 01012345678)" 
                    : "휴대폰번호를 입력하세요 (선택) (예: 01012345678)"
                }
                disabled={isLoading}
                maxLength={11}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">
                이메일
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder={
                  baseField === "email" 
                    ? "이메일을 입력하세요 (필수)" 
                    : "이메일을 입력하세요 (선택)"
                }
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="groupNo">회원 등급</Label>
              <Select
                key={`customer-group-${customerGroups.map(group => group.groupNo).join("-")}`}
                value={formData.groupNo ? String(formData.groupNo) : undefined}
                onValueChange={(value) => {
                  const parsed = Number(value);
                  if (Number.isNaN(parsed)) return;
                  handleInputChange("groupNo", parsed);
                }}
                disabled={isOptionLoading || customerGroups.length === 0 || !isSuperAdmin}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="회원 등급을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {customerGroups.length === 0 ? (
                    <SelectItem value="no-group" disabled>
                      등록된 등급이 없습니다.
                    </SelectItem>
                  ) : (
                    customerGroups.map((group) => (
                      <SelectItem key={group.groupNo} value={group.groupNo.toString()}>
                        {group.groupName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="isFixedGroup">회원 등급 고정 여부</Label>
              <Select
                value={formData.isFixedGroup}
                onValueChange={(value) => handleInputChange("isFixedGroup", value as "T" | "F")}
                disabled={isOptionLoading || !isSuperAdmin}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="고정 여부를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="T">고정</SelectItem>
                  <SelectItem value="F">미고정</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              {isLoading ? "등록 중..." : "등록"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
